import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { http, apiError } from "@/lib/api";
import { trackTrialPurchase } from "@/lib/metaPixel";
import { CheckCircle, ArrowRight, Lightning, ShieldCheck } from "@phosphor-icons/react";

/**
 * /join/upgrade — OTO (Lean Roadmap Call, $97 anchored $300).
 *
 * Post-trial-signup one-time offer. User is already authed via cookies from
 * /funnel/start-trial. On YES → Stripe one-time $97 → /join/book-call
 * (Calendly). On NO → straight to /join/welcome. No downsell.
 */

// TODO: paste your $300-value proof screenshot URL here (optional).
const VALUE_SCREENSHOT_URL = "";

const OTO_HEADLINE = "Kickstart your journey with a 1-on-1 Lean Roadmap Call with Coach Jimmy";
const OTO_SUBTITLE = "30 minutes to get you dead clear on your goals, your protocol, and the exact next 12 weeks.";
const OTO_PRICE_ANCHOR = "$300";
const OTO_PRICE_TODAY = "$97";
const OTO_DISCOUNT_PCT = 68; // ~($300-$97)/$300

export default function JoinUpgradePage() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();
  const [searchParams] = useSearchParams();

  // Fire Meta Pixel Purchase event for the $7 trial. Landing here means the
  // Stripe $7 checkout succeeded and Stripe redirected the user to
  // /join/upgrade?session_id=xxx. We guard against firing on refresh via
  // sessionStorage — Meta will dedupe anyway but this keeps the pixel logs clean.
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    const key = `_llm_pixel_trial_${sessionId}`;
    if (sessionStorage.getItem(key)) return;
    trackTrialPurchase();
    sessionStorage.setItem(key, "1");
  }, [searchParams]);

  const purchase = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await http.post("/funnel/oto-checkout", {
        oto: "roadmap-call",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setErr(apiError(e, "Couldn't start checkout. Try again or skip."));
      setBusy(false);
    }
  };

  const skip = () => nav("/join/welcome");

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="border-b border-[#262626] py-4 px-6 flex items-center justify-between">
        <div className="font-oswald text-2xl uppercase tracking-wider">
          Lazy<span className="text-[#FF3333]">Lean</span>
        </div>
        <div className="text-[10px] font-manrope uppercase tracking-[0.15em] text-zinc-500">
          Step <span className="text-[#FF3333]">2</span> of 2 · <span className="text-[#00CC66]">✓ You&apos;re in</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10 md:py-16">
        <div className="text-center mb-8">
          <div className="id-label text-[#FF3333] mb-3">— ONE-TIME OFFER · THIS PAGE ONLY —</div>
          <h1 className="font-oswald text-3xl md:text-5xl font-bold uppercase tracking-tight leading-tight max-w-3xl mx-auto">
            Wait — Fast-Track Your First 30 Days.
          </h1>
          <p className="text-zinc-300 font-manrope text-lg md:text-xl mt-6 max-w-2xl mx-auto">
            {OTO_HEADLINE}
          </p>
          <p className="text-zinc-500 font-manrope text-sm mt-3">{OTO_SUBTITLE}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 space-y-5">
            <div className="border border-[#FF3333]/50 bg-gradient-to-br from-[#FF3333]/10 to-transparent p-6">
              <div className="id-label text-[#FF3333] mb-3">— WHAT&apos;S INCLUDED —</div>
              <ul className="space-y-3">
                {[
                  <>30-minute 1-on-1 call with Coach Jimmy — over Zoom or WhatsApp, whichever you prefer.</>,
                  <>Get <span className="text-white font-semibold">dead clear on your goals</span> — where you&apos;re at, where you want to be, and the exact path in between.</>,
                  <>A <span className="text-white font-semibold">personalised protocol</span> for the first 30 days so you&apos;re not guessing what to eat, when to lift, or how to sleep better.</>,
                  <>Fast-track your results — no more googling protein timing at 2am or wondering if you&apos;re doing it right.</>,
                  <>Instant booking via Calendly the moment you check out.</>,
                ].map((t, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <CheckCircle size={20} weight="fill" className="text-[#FF3333] shrink-0 mt-0.5" />
                    <span className="text-zinc-200 font-manrope text-sm leading-relaxed">{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            {VALUE_SCREENSHOT_URL && (
              <div className="border border-[#262626] bg-[#0F0F0F] p-4" data-testid="value-screenshot-slot">
                <div className="id-label text-zinc-500 mb-2">— Proof of value: what others paid for this —</div>
                <img src={VALUE_SCREENSHOT_URL} alt="Value proof" className="w-full border border-[#262626]" />
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <div className="border border-[#FF3333] bg-[#0F0F0F] p-6 sticky top-6" data-testid="oto-primary-offer">
              <div className="id-label text-zinc-500 mb-2">— Lean Roadmap Call · 30min —</div>
              <div className="flex items-baseline gap-3 mb-1">
                <span className="text-zinc-500 line-through font-manrope text-lg">{OTO_PRICE_ANCHOR}</span>
                <span className="font-oswald text-5xl font-bold text-white">{OTO_PRICE_TODAY}</span>
              </div>
              <div className="text-[10px] font-manrope uppercase tracking-[0.15em] text-[#FF3333] mb-6">
                One-time · Save {OTO_DISCOUNT_PCT}% · This page only
              </div>

              <button
                onClick={purchase}
                disabled={busy}
                className="id-btn-primary w-full py-4 text-sm disabled:opacity-40"
                data-testid="oto-yes-button"
              >
                <Lightning size={16} weight="fill" /> Yes, Book My Roadmap Call <ArrowRight size={14} weight="bold" />
              </button>

              <button
                onClick={skip}
                disabled={busy}
                className="w-full mt-3 text-zinc-500 hover:text-white text-xs font-manrope uppercase tracking-[0.15em] py-2 disabled:opacity-40"
                data-testid="oto-no-thanks-button"
              >
                No thanks — take me into the app
              </button>

              {err && (
                <div className="mt-4 border border-[#FF3333] bg-[#FF3333]/10 px-3 py-2 text-xs text-[#FF3333] font-manrope" data-testid="oto-error">
                  {err}
                </div>
              )}

              <div className="mt-5 pt-4 border-t border-[#262626] flex items-center gap-2 text-[10px] text-zinc-500 font-manrope uppercase tracking-[0.15em]">
                <ShieldCheck size={12} weight="fill" className="text-[#FF3333]" /> Secure Stripe checkout · Same card as your trial
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
